import React, { FC } from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { usePlayer } from 'providers/player/PlayerContext';
import { getMiniPlayerProps } from 'utils/fnData';
import { PlayingAssetType } from 'types/Player';
import { DetailEpisode, MovieDetails } from 'types/Asset';
import translate from 'utils/fnTranslate';
import icons from 'style';
import { useNavigation } from 'hooks/useNavigation';
import YouboraTracking from 'providers/player/YouboraTracking';
import { getTrailerPlayerURL } from 'utils/fnUrl';
import { useAuth, useGuestAwareClickHandler } from 'providers/useAuth/AuthContext';
import { useApp } from 'providers/useApp/AppContext';
import { BookmarkSectionType } from 'types/Bookmarks';
import { useBookmarks } from 'providers/useBookmarks/BookmarksContext';
import { DetailPageScreenName } from 'types/Tracking';
import { BackButton, NavigationWrapper, StyledButton, StyledFavoriteButton } from './PageDetailCommon.css';

type NavigationProps = {
    overlayActive: boolean;
};

export const Navigation: FC<NavigationProps> = ({ overlayActive }) => {
    const { onBackPressed } = useNavigation();

    const handleBackClick = () => {
        onBackPressed('/', { skipSamePath: true });
    };

    useKeyboardShortcuts({
        backspace: {
            handler: handleBackClick,
            condition: () => !overlayActive,
        },
    });

    return (
        <NavigationWrapper>
            <BackButton
                type="button"
                data-test-id={'tv-detailscreen-backbutton'}
                aria-label={translate('BACK_BUTTON')}
                onClick={handleBackClick}
                tabIndex={0}
            >
                <SVGInline svg={icons.icoGalleryLeft} />
            </BackButton>
        </NavigationWrapper>
    );
};

export const TrailerButton: FC<{ asset: MovieDetails | DetailEpisode; screenName: DetailPageScreenName }> = ({ asset, screenName }) => {
    const { isMobile } = useApp();
    const { isGuest, isInhibited } = useAuth();
    const guestCheck = useGuestAwareClickHandler();
    const { isMini, updateMiniPlayerProps } = usePlayer();

    if (!asset.hasTrailer || isInhibited) {
        return null;
    }

    const trailerLink = isGuest || isMini ? null : getTrailerPlayerURL(asset.id);
    const updateMiniPlayer = () => {
        if (isMini) {
            updateMiniPlayerProps(getMiniPlayerProps(asset, PlayingAssetType.TRAILER));
        }
    };

    const handleTrailerClick = (event) => {
        guestCheck(event, () => {
            updateMiniPlayer();

            const youboraTracking = YouboraTracking.getInstance();
            youboraTracking.trackEngagement('Watch trailer', asset.id);
        });
    };

    return (
        <StyledButton
            key={'trailer'}
            label={translate('SCREEN_DETAIL_TRAILER_BUTTON')}
            link={trailerLink}
            isPrimary={false}
            onClick={handleTrailerClick}
            data-event-name={'click'}
            data-track-id={'button_trailer'}
            data-test-id={'button-trailer'}
            data-button-title={translate('SCREEN_DETAIL_TRAILER_BUTTON')}
            data-page-name={screenName}
        >
            {isMobile() && <SVGInline data-test-id={'trailer-playIcon'} svg={icons.playIconEpgHovered} />}
        </StyledButton>
    );
};

export const WorldButton: FC<{ worldId: string; type: BookmarkSectionType; screenName?: DetailPageScreenName; assetName?: string }> = ({
    worldId,
    type,
    assetName,
}) => {
    const { isBookmarked, toggleBookmarkItem } = useBookmarks();
    const { isGuest } = useAuth();

    const handleToggleFavorite = () => {
        toggleBookmarkItem(worldId, type);

        const youboraTracking = YouboraTracking.getInstance();
        const isFavorite = isBookmarked(worldId, type);
        const eventName = !isFavorite ? 'Favorite' : 'Remove favorite';

        const contentId = assetName ? `${worldId}_${assetName}` : worldId;

        youboraTracking.trackEngagement(eventName, contentId);
    };

    if (isGuest) return null;

    return (
        <StyledFavoriteButton
            isFavorite={isBookmarked(worldId, type)}
            key={'favorite-button'}
            isPrimary={false}
            assetName={assetName}
            onClick={handleToggleFavorite}
        />
    );
};
